import type { FormKind } from '@tunasong/music-lib'
import { FORM_POOL } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import type { FormSubMode } from './form-visualizer-params.js'
import styles from './form-visualizer-welcome.module.css'

export interface FormVisualizerWelcomeProps {
  preSelectedSubMode: FormSubMode | null
  preSelectedForm: FormKind | null
  onStartExplore: (form: FormKind) => void
  onStartQuiz: () => void
}

/**
 * Form Visualizer welcome surface. Two cards (Explore / Quiz); selecting
 * Explore reveals 4 form-kind cards in a 2×2 grid. Quiz fires immediately
 * on click (no form pre-selection needed — quiz draws randomly).
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §9
 */
export const FormVisualizerWelcome: FC<FormVisualizerWelcomeProps> = props => {
  const { preSelectedSubMode, preSelectedForm, onStartExplore, onStartQuiz } = props
  const [activeSubMode, setActiveSubMode] = useState<FormSubMode | null>(preSelectedSubMode)

  const toolCopy = useTheoryCopy('form-visualizer')
  const exploreCopy = useTheoryCopy('form-explore')
  const quizCopy = useTheoryCopy('form-id-quiz')

  const handleExploreCardClick = () => {
    setActiveSubMode('explore')
  }
  const handleQuizCardClick = () => {
    onStartQuiz()
  }

  return (
    <div className={styles.welcome}>
      <header className={styles.intro}>
        <h1 className={styles.title}>{toolCopy?.displayName ?? 'Form Visualizer'}</h1>
        {toolCopy?.body ? <p className={styles.tagline}>{toolCopy.body}</p> : null}
      </header>

      <div className={styles.subModeGrid}>
        <Button
          type="button"
          variant="outlined"
          onClick={handleExploreCardClick}
          aria-pressed={activeSubMode === 'explore'}
          className={styles.subModeCard}
        >
          <span className={styles.cardTitle}>{exploreCopy?.displayName ?? 'Form Explore'}</span>
          {exploreCopy?.body ? <span className={styles.cardBody}>{exploreCopy.body}</span> : null}
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={handleQuizCardClick}
          aria-pressed={preSelectedSubMode === 'quiz'}
          className={styles.subModeCard}
        >
          <span className={styles.cardTitle}>{quizCopy?.displayName ?? 'Form ID Quiz'}</span>
          {quizCopy?.body ? <span className={styles.cardBody}>{quizCopy.body}</span> : null}
        </Button>
      </div>

      {activeSubMode === 'explore' ? (
        <div className={styles.formGrid}>
          {FORM_POOL.map(form => (
            <FormCard
              key={form.kind}
              kind={form.kind}
              lexiconId={form.lexiconId}
              fallbackLabel={form.displayName}
              shortFallback={form.displayNameShort}
              preSelected={preSelectedForm === form.kind}
              onClick={() => onStartExplore(form.kind)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

interface FormCardProps {
  kind: FormKind
  lexiconId: string
  fallbackLabel: string
  shortFallback: string
  preSelected: boolean
  onClick: () => void
}

const FormCard: FC<FormCardProps> = props => {
  const copy = useTheoryCopy(props.lexiconId)
  return (
    <Button
      type="button"
      variant="outlined"
      onClick={props.onClick}
      aria-pressed={props.preSelected}
      className={styles.formCard}
    >
      <span className={styles.formCardShort}>{copy?.displayNameShort ?? props.shortFallback}</span>
      <span className={styles.formCardTitle}>{copy?.displayName ?? props.fallbackLabel}</span>
      {copy?.body ? <span className={styles.formCardBody}>{copy.body}</span> : null}
    </Button>
  )
}
